// Match Results — a plain, fixed date-descending fixture list (no sort/rank),
// ported 1:1 from js/renderers-matchresults.js via the 04b bridge. Competition +
// team pickers, a "Table" switch back to League Table, and the timezone-safe date
// format. Data via the Phase-02 useMatchResults hook.

import { leagueSelectGroups } from "./CountryFlag.js";
import { ModuleExplainer } from "./ModuleExplainer.js";
import { explainMatchResults } from "./explain.js";
import { LIVE } from "../live/flag.js";
import { useEffect, useState, type PointerEvent as ReactPointerEvent } from "react";
import { startMatchCardDragOnMove, startTeamTokenDragOnMove } from "../canvas/menu-drag.js";
import { type ProtoModuleProps, getModState, setModState, switchModuleType } from "./bridge.js";
import { SubControls } from "./SubControls.js";
import { StyledSelect } from "./StyledSelect.js";
import { useMatchResults, useMatchResultCompetitions } from "../hooks/useMatchResults.js";
import { useLeagueTableCompetitions } from "../hooks/useLeagueTable.js";
import { formatMatchDate, deriveTeamNames, selectFixtures } from "./match-results-view.js";
import { competitionLabel } from "./league-table-view.js";
import { competitionIdOf, type CompetitionId } from "../data/league.js";
import { useStickyClub } from "../hooks/useStickyClub.js";
import { useFollowedCompetition } from "../hooks/useFollowedCompetition.js";
import { matchOfResult } from "../live/match.js";
import type { ProtoFixture } from "@catbento/shared";

/** ?live=1: a result's team is a team you can pick up (teams and matches, Ben 2026-10-30) — onto
 *  empty canvas for its team card, onto a Bio to show it there. The newest season (the one shown). */
function onTeamPress(e: ReactPointerEvent<HTMLSpanElement>, id: number | undefined, name: string, competition: CompetitionId | null): void {
  if (!LIVE || id === undefined || competition === null) return;
  if (window.innerWidth <= 768 || e.button !== 0) return;
  e.stopPropagation();
  startTeamTokenDragOnMove(e.nativeEvent, { id, name, competitionId: competition, season: null });
}

/** ?live=1: a result's score is the match (the Match module, 2026-10-30) — onto empty canvas or
 *  a Match to show it. */
function onScorePress(e: ReactPointerEvent<HTMLSpanElement>, m: ProtoFixture, competition: CompetitionId | null): void {
  const match = LIVE && competition !== null ? matchOfResult(m, competition, m.seasonCode) : null;
  if (!match) return;
  if (window.innerWidth <= 768 || e.button !== 0) return;
  e.stopPropagation();
  startMatchCardDragOnMove(e.nativeEvent, match);
}

export function MatchResultsModule({ mod, body }: ProtoModuleProps) {
  const competitions = useMatchResultCompetitions();
  const stickyClub = useStickyClub();
  // The league's id (ids-2): the one it had if still listed, else (?live=1) the current
  // player's league, else the first. A selected one follows a linked pick.
  const opening = useFollowedCompetition(mod, "_matchResultsCompetition", competitions, stickyClub?.competitionId ?? null);
  const [competition, setCompetition] = useState<CompetitionId | null>(opening.competition);
  // After following a player: just their club's fixtures (Ben, 2026-10-16). A club name the
  // fixtures don't list falls back to "All Teams" below.
  const [team, setTeam] = useState<string>(() =>
    opening.followed ? (stickyClub?.teamName ?? "") : (getModState<string>(mod, "_matchResultsTeam") ?? ""),
  );

  const fixtures = useMatchResults(competition);
  // The "Table" button shows for a league the League Table can open — so its list, not the
  // snapshot's (with ?live=1 the table is live; without it, the two are the same list).
  const leagueCompetitions = useLeagueTableCompetitions();
  const teamNames = deriveTeamNames(fixtures);
  // A team picked for another competition has no meaning here — fall back to "all".
  const effectiveTeam = team && teamNames.includes(team) ? team : "";
  const matches = selectFixtures(fixtures, effectiveTeam);

  useEffect(() => {
    body.style.justifyContent = competitions.length ? "flex-start" : "";
  }, [body, competitions.length]);

  // Write the resolved selection back as `_`-state (saveState/undo/presets capture it).
  useEffect(() => {
    if (competition !== null) setModState(mod, "_matchResultsCompetition", competition);
    setModState(mod, "_matchResultsTeam", effectiveTeam || undefined);
  }, [mod, competition, effectiveTeam]);

  if (!competitions.length) {
    return <div className="stats-empty">No match results captured.</div>;
  }

  // A pick goes onto the `_`-state AT ONCE (the T2-1 pattern): with ?live=1 the new
  // results may still be loading — or fail — and Retry rebuilds the widget from its
  // `_`-state, so it must retry the pick. (The snapshot site: the effect writes the same.)
  const onCompetition = (value: string) => {
    const next = competitionIdOf(value);
    setModState(mod, "_matchResultsCompetition", next);
    setModState(mod, "_matchResultsTeam", undefined);
    setCompetition(next);
    setTeam(""); // a team from another competition doesn't exist here
  };
  const onTable = () => {
    setModState(mod, "_leagueTableCompetition", competition);
    switchModuleType(mod, "League Table");
  };

  const subControls = (
    <SubControls mod={mod} body={body}>
      <StyledSelect
        // Live: grouped and searchable — top leagues, then by country, with flags (Ben, 2026-10-18).
        items={LIVE ? leagueSelectGroups(competitions, competition) : competitions.map((c) => ({ value: String(c.id), label: c.label }))}
        value={competition === null ? "" : String(competition)}
        onChange={onCompetition}
        wrapClass="match-results-competition-select-wrap"
        extraClass="match-results-competition-select"
        searchable={LIVE}
      />
      <StyledSelect
        items={[{ value: "", label: "All Teams" }, ...teamNames.map((t) => ({ value: t, label: t }))]}
        value={effectiveTeam}
        onChange={setTeam}
        wrapClass="match-results-team-select-wrap"
        extraClass="match-results-team-select"
        searchable={teamNames.length > 12}
      />
      {leagueCompetitions.some((c) => c.id === competition) && (
        <button type="button" className="module-switch-btn" onClick={onTable}>
          Table
        </button>
      )}
    </SubControls>
  );

  if (!matches.length) {
    const teamPart = effectiveTeam ? ` for ${effectiveTeam}` : "";
    return (
      <>
        {subControls}
        <div className="stats-empty">
          No match results captured{teamPart} in {competitionLabel(competitions, competition)}.
        </div>
      </>
    );
  }

  return (
    <>
      {LIVE && (
        <ModuleExplainer
          mod={mod}
          paragraphs={explainMatchResults({
            competition: competitionLabel(competitions, competition),
            team: effectiveTeam || null,
            count: matches.length,
          })}
        />
      )}
      {subControls}
      <div className="data-wrapper top-align">
        {matches.map((m) => (
          <div key={m.matchId} className="data-row match-row">
            <span className="match-date">{formatMatchDate(m.matchDate)}</span>
            <span
              className={LIVE && m.homeTeamId !== undefined ? "match-home team-draggable" : "match-home"}
              onPointerDown={(e) => onTeamPress(e, m.homeTeamId, m.homeTeamName, competition)}
            >
              {m.homeTeamName}
            </span>
            <span
              className={LIVE && m.seasonCode !== undefined ? "match-score team-draggable" : "match-score"}
              onPointerDown={(e) => onScorePress(e, m, competition)}
            >
              {m.homeScore} – {m.awayScore}
            </span>
            <span
              className={LIVE && m.awayTeamId !== undefined ? "match-away team-draggable" : "match-away"}
              onPointerDown={(e) => onTeamPress(e, m.awayTeamId, m.awayTeamName, competition)}
            >
              {m.awayTeamName}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}
